import React from "react";
import { Link } from "react-router-dom";
import { Shield, Users, Clock, Phone, CheckCircle, Accessibility, Heart, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const Services = () => {
  const services = [
    {
      icon: <Accessibility className="h-8 w-8 text-blue-900" />,
      title: "Wheelchair Accessible Transportation",
      description: "Fully equipped vehicles with wheelchair lifts and secure wheelchair restraint systems for safe and comfortable transportation.",
      features: ["Professional wheelchair lifts", "Secure restraint systems", "Climate-controlled vehicles", "Easy boarding assistance"]
    },
    {
      icon: <Users className="h-8 w-8 text-blue-900" />,
      title: "Ambulatory Transportation",
      description: "Comfortable transportation for passengers who can walk but need assistance getting to medical appointments or other destinations.",
      features: ["Door-to-door service", "Assistance with mobility", "Comfortable seating", "Professional drivers"]
    },
    {
      icon: <MapPin className="h-8 w-8 text-blue-900" />,
      title: "Local & Long-Distance Trips",
      description: "Whether you need transportation within Calhoun County or to distant medical facilities, we provide reliable service for any distance.",
      features: ["Local area coverage", "Long-distance trips", "24-hour advance notice preferred", "Flexible scheduling"]
    },
    {
      icon: <Heart className="h-8 w-8 text-blue-900" />,
      title: "Medical Appointment Transportation",
      description: "Specialized transportation to medical appointments, dialysis centers, physical therapy, and other healthcare facilities.",
      features: ["Appointment reminders", "On-time guarantee", "Medical equipment transport", "Healthcare facility knowledge"]
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Schedule Your Ride",
      description: "Call us or use our online booking form to schedule your transportation. We prefer 24 hours notice for long-distance trips."
    },
    {
      step: "2",
      title: "Confirmation & Reminder",
      description: "We'll confirm your booking details and send appointment reminders to ensure you're prepared for your trip."
    },
    {
      step: "3",
      title: "Safe Transportation",
      description: "Our professional drivers arrive on time and provide door-to-door service with the care and respect you deserve."
    },
    {
      step: "4",
      title: "Return Trip (if needed)",
      description: "We can arrange return transportation to get you safely back home after your appointment or event."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Transportation Services</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Quality Transport Services provides comprehensive Non-Emergency Medical Transportation 
              with a focus on safe, reliable, and respectful service for all our clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/4923383/pexels-photo-4923383.jpeg"
                alt="Wheelchair accessible van"
                className="rounded-lg shadow-xl w-full h-80 object-cover"
              />
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <Shield className="h-6 w-6 text-green-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Insurance & Self-Pay Options</h3>
                  <p className="text-gray-600">We primarily transport for insurance-covered trips but also welcome self-pay clients.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Clock className="h-6 w-6 text-green-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Flexible Scheduling</h3>
                  <p className="text-gray-600">24-hour advance notice preferred for long-distance trips, but we accommodate last-minute requests when possible.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Users className="h-6 w-6 text-green-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900">Veteran-Owned & Family-Operated</h3>
                  <p className="text-gray-600">Founded by veterans who understand service, reliability, and treating every client with respect and dignity.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Transportation Services We Provide</h2>
            <p className="text-lg text-gray-600">
              Comprehensive medical transportation solutions tailored to your specific needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-3">
                    {service.icon}
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </div>
                  <p className="text-gray-600">{service.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How Our Transportation Works</h2>
            <p className="text-lg text-gray-600">
              A simple, reliable process designed around your comfort and convenience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-blue-900 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Service Areas</h2>
          <p className="text-lg text-gray-600 mb-8">
            We proudly serve Calhoun County and surrounding areas with reliable medical transportation services.
          </p>
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Primary Service Area</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Calhoun County</li>
                  <li>• Battle Creek</li>
                  <li>• Marshall</li>
                  <li>• Albion</li>
                  <li>• Homer</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Extended Coverage</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Surrounding counties</li>
                  <li>• Long-distance medical facilities</li>
                  <li>• Specialized treatment centers</li>
                  <li>• Airport transportation</li>
                  <li>• Custom destination requests</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Transportation?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Experience the difference of veteran-owned, family-operated transportation services 
            that put your safety, comfort, and dignity first.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-amber-500 hover:bg-amber-600 text-white">
              <Link to="/book-ride">Book Your Ride</Link>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
              <Phone className="h-4 w-4 mr-2" />
              Call (269) 248-3514
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;